import { useMutation, useQuery } from "@tanstack/react-query";
import { deleteApi, get, post, put } from "../../api/client";
import { dataToOptionsTransformer } from "../../Services/transformer/dataToOptionsTransformer";
import queryClient from "../../config/queryClient";
import { ClassPayloadInterface } from "../../Constants/Interfaces/payloadInterface/ClassPayload.interface";

export const CLASS_KEY = {
  CLASS_LIST: "CLASS_LIST",
  CLASS_LIST_OPTIONS: "CLASS_LIST_OPTIONS",
};
export const useGetClasses = () =>
  useQuery([CLASS_KEY.CLASS_LIST], () => get({ url: "api/v1/classes" }));

export const useGetClassesOptions = () => {
  return useQuery([CLASS_KEY.CLASS_LIST_OPTIONS], () => {
    const classOptions = get({ url: "api/v1/classes" }).then((classes) => {
      return dataToOptionsTransformer({
        dataToTransform: classes,
        value: "id",
        text: "name",
      });
    });
    return classOptions;
  });
};

export const useAddClass = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => unknown;
  onError: (error: Error) => unknown;
}) =>
  useMutation(
    (data: ClassPayloadInterface) => {
      return post({
        url: "api/v1/classes/add",
        body: data,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [CLASS_KEY.CLASS_LIST],
        });
      },
      onError,
    }
  );

export const useUpdateClass = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => unknown;
  onError: (error: Error) => unknown;
}) =>
  useMutation(
    ({ classId, body }: { classId: number; body: ClassPayloadInterface }) => {
      return put({
        url: "api/v1/classes/update",
        body,
        params: { classId },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [CLASS_KEY.CLASS_LIST],
        });
        onSuccess();
      },
      onError: (error: Error) => onError(error),
    }
  );

export const useRemoveClass = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => unknown;
  onError: (error: Error) => unknown;
}) => {
  return useMutation(
    (classId: number) => {
      const parameters = {
        classId,
      };
      const includeToken = true;
      return deleteApi(`api/v1/classes/remove`, includeToken, parameters);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [CLASS_KEY.CLASS_LIST],
        });
        onSuccess();
      },
      onError: (error: Error) => {
        onError(error);
      },
    }
  );
};
